import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Sparkles, Brain, Trophy, AlertTriangle, Target } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { OpportunityCard } from "@/components/intelligence/OpportunityCard";
import { KpiTile } from "@/components/intelligence/KpiTile";
import { OpportunitiesFilterBar } from "@/components/intelligence/OpportunitiesFilterBar";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { opportunitiesService } from "@/services/opportunities.service";
import { formatCLP } from "@/lib/mock-data";
import type { Recommendation, Riesgo } from "@/types";

export const Route = createFileRoute("/oportunidades/")({
  head: () => ({
    meta: [
      { title: "Oportunidades Inteligentes — LicitaPro" },
      {
        name: "description",
        content:
          "Motor de recomendación IA: matching, scoring y probabilidad de adjudicación por licitación.",
      },
      { property: "og:title", content: "Oportunidades Inteligentes — LicitaPro" },
      {
        property: "og:description",
        content: "Copiloto estratégico de adjudicaciones para licitaciones públicas en Chile.",
      },
    ],
  }),
  component: OportunidadesPage,
});

const PAGE_SIZE = 50;


function OportunidadesPage() {
  // null = usar el umbral configurado en el perfil del tenant.
  const [minScore, setMinScore] = useState<number | null>(null);
  const [minScoreInput, setMinScoreInput] = useState<number | null>(null);
  const [rec, setRec] = useState<Recommendation[]>([]);
  const [riesgos, setRiesgos] = useState<Riesgo[]>([]);
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["oportunidades", "list", page, minScore],
    queryFn: () =>
      opportunitiesService.list(
        minScore != null ? { minScore } : undefined,
        page,
        PAGE_SIZE,
      ),
    placeholderData: (prev) => prev,
    // Mientras el embudo corre por primera vez, refrescamos para ver el avance.
    refetchInterval: (q) => (q.state.data?.estadoProceso.procesando ? 4000 : false),
  });

  const recalcular = useMutation({
    mutationFn: () => opportunitiesService.procesar("full"),
    onSuccess: (r) => {
      toast.success(
        r.yaEnCurso
          ? "Ya hay un recálculo en curso"
          : "Recalculando todas las oportunidades con tu perfil",
      );
      void qc.invalidateQueries({ queryKey: ["oportunidades"] });
    },
    onError: () => toast.error("No se pudo iniciar el recálculo"),
  });

  const oportunidades = useMemo(() => query.data?.items ?? [], [query.data]);
  const embudo = query.data?.embudo;
  const proceso = query.data?.estadoProceso;
  const totalSobreUmbral = query.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalSobreUmbral / PAGE_SIZE));
  const umbralActual = minScoreInput ?? minScore ?? embudo?.umbral ?? 0;

  const filtered = useMemo(
    () =>
      oportunidades.filter((o) => {
        if (rec.length && !rec.includes(o.scoring.recommendation)) return false;
        if (riesgos.length && !riesgos.includes(o.scoring.riesgo)) return false;
        return true;
      }),
    [oportunidades, rec, riesgos],
  );

  const recomendadas = oportunidades.filter((o) => o.scoring.recommendation === "postular");
  const monto = recomendadas.reduce((a, o) => a + o.licitacion.montoEstimado, 0);
  const avg = Math.round(
    oportunidades.reduce((a, o) => a + o.scoring.score, 0) / Math.max(oportunidades.length, 1),
  );
  const riesgosAltos = oportunidades.filter((o) => o.scoring.riesgo === "alto").length;

  function toggle<T>(arr: T[], v: T, setter: (n: T[]) => void) {
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  /** Aplica el umbral en el servidor (el listado se filtra en la BD, no en pantalla). */
  function aplicarUmbral() {
    if (minScoreInput == null) return;
    setPage(1);
    setMinScore(minScoreInput);
  }



  return (
    <AppShell
      title="Oportunidades Inteligentes"
      subtitle="Motor de recomendación · Analizadas con tu perfil empresarial"
      actions={
        <div className="hidden md:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground">
          <Brain className="size-3.5 text-primary" />
          IA activa
        </div>
      }
    >
      {/* Franja del copiloto */}
      <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-2.5 mb-3">
        <div className="absolute inset-0 grid-pattern opacity-[0.03]" />
        <div className="relative flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="size-7 rounded-md bg-gradient-primary grid place-items-center shadow-glow shrink-0">
              <Sparkles className="size-4 text-primary-foreground" />
            </div>
            <div className="text-sm text-foreground truncate">
              El copiloto recomienda{" "}
              <span className="font-medium text-primary">{recomendadas.length} licitaciones</span>{" "}
              con alto match
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4 text-[11px] text-muted-foreground">
            <span>
              Score promedio{" "}
              <span className="text-foreground font-medium tabular-nums">{avg}</span>
            </span>
            <span>
              Potencial <span className="text-foreground font-medium">{formatCLP(monto)}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Embudo del motor — mismo formato que Compra Ágil IA: va sobre las
          tarjetas, no dentro de la de "Sobre umbral", que muestra sólo su dato. */}
      {embudo && (
        <div className="rounded-xl border border-border bg-card p-4 mb-6">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="text-foreground font-medium tabular-nums">
              {embudo.vigentes.toLocaleString("es-CL")}
            </span>
            vigentes
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.prefiltradas.toLocaleString("es-CL")}
            </span>
            compatibles con tu perfil
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.sobreUmbral.toLocaleString("es-CL")}
            </span>
            sobre umbral ({embudo.umbral}){" "}
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.evaluadasIa.toLocaleString("es-CL")}
            </span>
            con análisis IA
          </div>
          {proceso?.procesando && (
            <div className="mt-2 text-xs text-primary tabular-nums">
              Procesando el universo… {proceso.listas.toLocaleString("es-CL")} /{" "}
              {proceso.total.toLocaleString("es-CL")}
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiTile
          icon={Target}
          label="Sobre umbral"
          value={totalSobreUmbral}
          accent="primary"
          hint="Compatibles con tu perfil"
        />
        <KpiTile
          icon={Trophy}
          label="Recomendadas"
          value={recomendadas.length}
          accent="success"
          hint="Recomendación: POSTULAR"
        />
        <KpiTile
          icon={Brain}
          label="Score promedio"
          value={avg}
          accent="info"
          hint="Compatibilidad global"
        />
        <KpiTile
          icon={AlertTriangle}
          label="Riesgos altos"
          value={riesgosAltos}
          accent="destructive"
          hint="Requieren revisión humana"
        />
      </div>

      {/* Filtros */}
      <OpportunitiesFilterBar
        umbral={umbralActual}
        umbralPersonalizado={minScore != null}
        onUmbralChange={setMinScoreInput}
        onUmbralCommit={aplicarUmbral}
        onUmbralReset={() => {
          setMinScoreInput(null);
          setMinScore(null);
          setPage(1);
        }}
        rec={rec}
        onToggleRec={(v) => toggle(rec, v, setRec)}
        riesgos={riesgos}
        onToggleRiesgo={(v) => toggle(riesgos, v, setRiesgos)}
        onLimpiar={() => {
          setRec([]);
          setRiesgos([]);
          setMinScoreInput(null);
          setMinScore(null);
          setPage(1);
        }}
        onRecalcular={() => recalcular.mutate()}
        recalculando={recalcular.isPending || Boolean(proceso?.procesando)}
        mostrando={filtered.length}
        total={totalSobreUmbral}
        page={page}
        totalPaginas={totalPaginas}
      />


      {/* Grid */}
      {query.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl border border-border bg-card animate-pulse" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No se pudieron cargar las oportunidades"
          description="Revisa tu sesión o vuelve a intentarlo en unos segundos."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={
            proceso?.procesando
              ? "Procesando tus oportunidades"
              : totalSobreUmbral === 0
                ? "Aún no hay licitaciones sobre tu umbral"
                : "Sin oportunidades con los filtros actuales"
          }
          description={
            proceso?.procesando
              ? "Estamos cruzando todo el universo vigente con el perfil de tu empresa. Esto puede tardar unos minutos la primera vez."
              : totalSobreUmbral === 0
                ? "Baja el score mínimo, completa el perfil de tu empresa o pulsa «Recalcular todo» para volver a puntuar el universo vigente."
                : "Ajusta el score mínimo o quita filtros para ver más resultados."
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((o) => (
              <OpportunityCard key={o.licitacion.id} item={o} />
            ))}
          </div>
          {totalPaginas > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {page} / {totalPaginas}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                disabled={page >= totalPaginas || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}

    </AppShell>
  );
}
